import type { NativeSequenceStepForm, NativeWaitUnit } from '@/types/plays';

/**
 * Native LinkedIn sequence (v1): one LinkedIn Outreach node that carries the whole invite → message
 * sequence, with LinkedIn itself waiting for each person to accept before the messages go out.
 *
 * A play is native when its custom_action_details carries `native_sequence`. Anything else is a
 * legacy single-touch node and keeps every existing code path. The rules below mirror the shared
 * contract enforced by mb-server's schema and audience-processor's runtime (reject, never coerce):
 *
 * - steps[0] is a connection request, followed by 1 to 3 messages; nothing else.
 * - every step_id is unique and matches `s1`..`s9`, stable for the life of the play.
 * - every message carries a wait of 3 hours to 30 days, in HOUR or DAY. On the first message it
 *   counts from acceptance; on later messages it counts from the previous message.
 * - a step's copy is verbatim text, or exactly `{{content}}` with a brief for the AI. The invite
 *   note may be empty (no note) and is capped at 300 characters.
 * - exactly one pinned sender.
 *
 * Once anyone has been enrolled the server sets `native_sequence.frozen_at`, after which the steps
 * and sender never change; the campaign is cloned instead.
 */

interface NativeSequenceWait {
  value: number;
  unit: NativeWaitUnit;
}

const NATIVE_SEQUENCE_VERSION = 1;
const CONNECTION_REQUEST = 'heyreach_send_connection_request';
const SEND_MESSAGE = 'heyreach_send_message';
const MIN_MESSAGES = 1;
const MAX_MESSAGES = 3;
const MIN_WAIT_HOURS = 3;
const MAX_WAIT_DAYS = 30;
const STEP_ID_PATTERN = /^s[1-9]$/;

/** The exact template that asks for per-person AI copy on a native step. */
export const NATIVE_AI_CONTENT_TOKEN = '{{content}}';
/** LinkedIn's cap on a connection note. */
export const NATIVE_CONNECTION_NOTE_MAX = 300;
/** The cap on a native message step's text, shared with mb-server and audience-processor. */
export const NATIVE_MESSAGE_MAX = 8000;
/** `{{content}}` anywhere, in any spacing or casing — only the exact token alone is allowed. */
const ANY_CONTENT_TOKEN = /\{\{\s*content\s*\}\}/i;

type Details = Record<string, unknown> | null | undefined;
type RawStep = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

/**
 * @param details - A play's custom_action_details
 * @returns True when the play is a native LinkedIn sequence
 */
export const isNativeLinkedInSequence = (details: Details): boolean => (
  // The key's presence decides, as on the server: a malformed marker is a native play that fails
  // validation, never a legacy play the single-step editor may rewrite.
  isRecord(details) && Object.prototype.hasOwnProperty.call(details, 'native_sequence')
);

/**
 * @param details - A play's custom_action_details
 * @returns True when the native sequence has enrolled people and can no longer change
 */
export const isFrozenNativeSequence = (details: Details): boolean => {
  if (!isNativeLinkedInSequence(details)) return false;
  const marker = details!.native_sequence;
  return isRecord(marker) && marker.frozen_at !== null && marker.frozen_at !== undefined;
};

const isIsoTimestamp = (value: unknown): boolean => (
  typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Date.parse(value))
);

/**
 * @param details - A play's custom_action_details
 * @returns The persisted steps, in order (non-object entries are kept as empty objects)
 */
export const nativeSequenceSteps = (details: Details): RawStep[] => {
  const steps = isRecord(details) && Array.isArray(details.steps) ? details.steps : [];
  return steps.map((step) => (isRecord(step) ? step : {}));
};

/**
 * The canvas and run-history label for a native node: "LinkedIn sequence (3 steps)".
 *
 * @param details - A native play's custom_action_details
 * @returns The label
 */
export const nativeSequenceLabel = (details: Details): string => {
  const count = nativeSequenceSteps(details).length;
  return `LinkedIn sequence (${count} step${count === 1 ? '' : 's'})`;
};

/**
 * @param wait - A message step's persisted wait
 * @returns Why the wait is invalid, or null when it is valid
 */
export const validateNativeWait = (wait: unknown): string | null => {
  if (!isRecord(wait)) return 'Set how long to wait before this message.';
  const { value, unit } = wait;
  if (unit !== 'HOUR' && unit !== 'DAY') return 'Choose hours or days for the wait.';
  if (typeof value !== 'number' || !Number.isInteger(value)) return 'The wait must be a whole number.';
  const hours = unit === 'DAY' ? value * 24 : value;
  if (hours < MIN_WAIT_HOURS) return `The wait must be at least ${MIN_WAIT_HOURS} hours.`;
  if (hours > MAX_WAIT_DAYS * 24) return `The wait can't be longer than ${MAX_WAIT_DAYS} days.`;
  return null;
};

/**
 * "2 days after acceptance" / "3 hours after the previous message".
 *
 * @param wait - A message step's persisted wait
 * @param isFirstMessage - Whether this is the first message after the invite
 * @returns The wait in a rep's words
 */
export const describeNativeWait = (wait: unknown, isFirstMessage: boolean): string => {
  const anchor = isFirstMessage ? 'after acceptance' : 'after the previous message';
  if (!isRecord(wait) || typeof wait.value !== 'number' || (wait.unit !== 'HOUR' && wait.unit !== 'DAY')) {
    return `Wait not set (${anchor})`;
  }
  const noun = wait.unit === 'DAY' ? 'day' : 'hour';
  return `${wait.value} ${noun}${wait.value === 1 ? '' : 's'} ${anchor}`;
};

const templateOf = (step: RawStep): string => (
  typeof step.message_template === 'string' ? step.message_template : ''
);
const guidanceOf = (step: RawStep): string => (
  typeof step.message_guidance === 'string' ? step.message_guidance : ''
);

/**
 * Why a step's copy is invalid, or null. Shared by the persisted-shape validator and the editor.
 */
const copyError = (template: unknown, guidance: string, isInvite: boolean): string | null => {
  if (typeof template !== 'string') return 'This step has no message text.';
  if (template === NATIVE_AI_CONTENT_TOKEN) {
    return guidance.trim() ? null : 'Give the AI a brief for this step.';
  }
  // Text mixed with the AI token ("Hello {{content}}") is neither verbatim nor AI copy; the
  // server rejects it, so the editor does too.
  if (ANY_CONTENT_TOKEN.test(template)) {
    return 'Use "Let AI write it" instead of putting {{content}} in your text.';
  }
  if (isInvite) {
    return template.length > NATIVE_CONNECTION_NOTE_MAX
      ? `LinkedIn caps connection notes at ${NATIVE_CONNECTION_NOTE_MAX} characters.`
      : null;
  }
  if (!template.trim()) return 'Write the message, or let the AI write it.';
  return template.length > NATIVE_MESSAGE_MAX
    ? `Messages are capped at ${NATIVE_MESSAGE_MAX.toLocaleString('en-US')} characters.`
    : null;
};

/**
 * Every way a native play breaks the v1 contract, in plain words. Empty means valid.
 *
 * @param details - A native play's custom_action_details
 * @returns The problems found
 */
export const validateNativeSequence = (details: Details): string[] => {
  if (!isNativeLinkedInSequence(details)) return ['This is not a LinkedIn sequence.'];
  const nativeSequence = details!.native_sequence;
  if (!isRecord(nativeSequence)) return ['The LinkedIn sequence settings are malformed.'];
  const errors: string[] = [];
  if (nativeSequence.version !== NATIVE_SEQUENCE_VERSION) {
    errors.push('The LinkedIn sequence version is not supported.');
  }
  if (nativeSequence.frozen_at != null && !isIsoTimestamp(nativeSequence.frozen_at)) {
    errors.push('The LinkedIn sequence has an invalid enrollment time.');
  }

  const steps = nativeSequenceSteps(details);
  const messageCount = steps.length - 1;
  if (steps[0]?.action_type !== CONNECTION_REQUEST) {
    errors.push('A LinkedIn sequence must start with a connection request.');
  }
  if (messageCount < MIN_MESSAGES || messageCount > MAX_MESSAGES) {
    errors.push(`A LinkedIn sequence has ${MIN_MESSAGES} to ${MAX_MESSAGES} messages after the connection request.`);
  }

  const seenIds = new Set<string>();
  steps.forEach((step, index) => {
    const position = `Step ${index + 1}`;
    const stepId = step.step_id;
    if (typeof stepId !== 'string' || !STEP_ID_PATTERN.test(stepId) || seenIds.has(stepId)) {
      errors.push(`${position} has a missing or duplicate step id.`);
    } else {
      seenIds.add(stepId);
    }
    if (index === 0) {
      if (step.wait != null) {
        errors.push('Step 1: The connection request has no wait; waits belong to the messages after it.');
      }
      if (step.send_without_note === true && typeof step.message_template === 'string' && step.message_template.trim()) {
        errors.push('Step 1: A connection request sent with no note can\'t also have a note.');
      }
    } else {
      if (step.action_type !== SEND_MESSAGE) {
        errors.push(`${position} must be a message.`);
      }
      const waitError = validateNativeWait(step.wait);
      if (waitError) errors.push(`${position}: ${waitError}`);
    }
    // A missing template is invalid, not "no note": only an explicit "" means no note.
    const problem = copyError(step.message_template, guidanceOf(step), index === 0);
    if (problem) errors.push(`${position}: ${problem}`);
  });

  const senderIds = Array.isArray(details!.linkedInAccountIds) ? details!.linkedInAccountIds : [];
  if (senderIds.length !== 1 || Number(senderIds[0]) !== Number(details!.linkedInAccountId)) {
    errors.push('A LinkedIn sequence needs exactly one sender.');
  }
  if (details!.linkedin_sender_selection != null) {
    errors.push('A LinkedIn sequence uses its one sender; AI sender routing isn\'t supported.');
  }
  return errors;
};

/**
 * The side panel's editable state for each persisted step.
 *
 * @param details - A native play's custom_action_details
 * @returns One form entry per step, in order
 */
export const buildNativeStepForms = (details: Details): NativeSequenceStepForm[] => (
  nativeSequenceSteps(details).map((step, index) => {
    const template = templateOf(step);
    const isInvite = index === 0;
    const mode: NativeSequenceStepForm['message_mode'] = template === NATIVE_AI_CONTENT_TOKEN
      ? 'ai'
      : isInvite && template === '' ? 'none' : 'static';
    const wait = isRecord(step.wait) ? step.wait : undefined;
    return {
      message_mode: mode,
      message_template: mode === 'static' ? template : '',
      message_guidance: guidanceOf(step),
      ...(isInvite ? {} : {
        wait_value: typeof wait?.value === 'number' ? wait.value : '',
        wait_unit: wait?.unit === 'HOUR' ? 'HOUR' : 'DAY',
      }),
    };
  })
);

const formWaitOf = (form: NativeSequenceStepForm): NativeSequenceWait => ({
  // Blank stays NaN (never 0), so an emptied field reads as invalid rather than "no wait".
  value: form.wait_value === '' || form.wait_value === undefined ? Number.NaN : Number(form.wait_value),
  unit: form.wait_unit === 'HOUR' ? 'HOUR' : 'DAY',
});

const formTemplateOf = (form: NativeSequenceStepForm, isInvite: boolean): string => {
  if (form.message_mode === 'ai') return NATIVE_AI_CONTENT_TOKEN;
  if (form.message_mode === 'none' && isInvite) return '';
  return form.message_template ?? '';
};

/**
 * What is wrong with one step as the user has it in the side panel, or null.
 *
 * @param form - The step's editable state
 * @param index - The step's position (0 is the invite)
 * @returns The problems, keyed by field
 */
export const nativeStepFormErrors = (
  form: NativeSequenceStepForm | undefined,
  index: number,
): { copy: string | null; wait: string | null } => {
  if (!form) return { copy: null, wait: null };
  const isInvite = index === 0;
  return {
    copy: copyError(formTemplateOf(form, isInvite), form.message_guidance ?? '', isInvite),
    wait: isInvite ? null : validateNativeWait(formWaitOf(form)),
  };
};

/**
 * The sender the panel's choice pins: the chosen seat when it is a positive id, else none (the
 * persisted sender stays).
 */
const chosenSeatOf = (senderId: string | undefined): number | null => {
  const id = Number(senderId);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * The sequence the user's edits produce: the persisted steps with their copy and waits replaced,
 * and the chosen sender pinned, every other key kept. Null when the sequence is frozen or the edit
 * would add or remove a step.
 */
const applyNativeEdits = (
  base: Details,
  forms: NativeSequenceStepForm[] | undefined,
  senderId?: string,
): Record<string, unknown> | null => {
  if (!isNativeLinkedInSequence(base) || isFrozenNativeSequence(base)) return null;
  const baseSteps = nativeSequenceSteps(base);
  if (!Array.isArray(forms) || forms.length !== baseSteps.length) return null;

  const steps = baseSteps.map((baseStep, index) => {
    const form = forms[index];
    const isInvite = index === 0;
    const template = formTemplateOf(form, isInvite);
    const step: RawStep = { ...baseStep, message_template: template };
    // Choosing a note on a note-free invite retires its flag. A flag persisted beside a note is
    // already invalid and stays as is, so it is reported, never quietly fixed.
    if (isInvite && form.message_mode !== 'none' && templateOf(baseStep) === '') {
      delete step.send_without_note;
    }
    if (template === NATIVE_AI_CONTENT_TOKEN) {
      step.message_guidance = (form.message_guidance ?? '').trim();
    } else {
      // A brief only steers AI copy; on verbatim text or no note it would do nothing.
      delete step.message_guidance;
    }
    if (!isInvite) step.wait = formWaitOf(form);
    return step;
  });

  const seat = chosenSeatOf(senderId);
  return {
    ...base!,
    steps,
    ...(seat === null ? {} : { linkedInAccountId: seat, linkedInAccountIds: [seat] }),
  };
};

/**
 * Why the user's edits can't be saved — every contract problem of the sequence they would produce
 * (including ones already on the persisted play, such as a wait on the invite or AI sender
 * routing, which the side panel can't fix). Empty means the edits save.
 *
 * @param base - The play's persisted custom_action_details
 * @param forms - The side panel's state, one entry per step
 * @param senderId - The seat chosen in the panel; omitted or blank keeps the persisted sender
 * @returns The problems, in plain words
 */
export const nativeSequenceEditErrors = (
  base: Details,
  forms: NativeSequenceStepForm[] | undefined,
  senderId?: string,
): string[] => {
  const candidate = applyNativeEdits(base, forms, senderId);
  return candidate ? validateNativeSequence(candidate) : [];
};

/**
 * The details to save after the user edits a native node's copy or waits. Every step is kept, in
 * order, with its id and type; only its copy and wait change. Every other key on the play is kept
 * as is.
 *
 * The chosen sender, when given, replaces the pinned seat (the server allows a teammate's seat for
 * admins only).
 *
 * Returns null — nothing is saved — when the sequence is frozen, when the edit would add or remove a
 * step, or when the result would break the contract. A native play is never written back as a
 * single step.
 *
 * @param base - The play's persisted custom_action_details
 * @param forms - The side panel's state, one entry per step
 * @param senderId - The seat chosen in the panel; omitted or blank keeps the persisted sender
 * @returns The details to save, or null
 */
export const buildNativeCustomDetails = (
  base: Details,
  forms: NativeSequenceStepForm[] | undefined,
  senderId?: string,
): Record<string, unknown> | null => {
  const next = applyNativeEdits(base, forms, senderId);
  return next && validateNativeSequence(next).length === 0 ? next : null;
};
